import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Page } from '../../components';
import DhobaraPageLayout from '../../components/DhobaraPageLayout/DhobaraPageLayout';
import css from './HowItWorksPage.module.css';

const SELLER_STEPS = [
  {
    label: 'STEP 1',
    title: 'Submit Your Listing',
    body: 'Fill out our simple listing form with details about your piece — designer, condition, size, and your asking price. It takes under 2 minutes.',
  },
  {
    label: 'STEP 2',
    title: 'We Perfect Your Photos',
    body: 'Upload your photos and our AI cleans them up — background removal, colour correction, and a studio-quality finish that makes your piece shine.',
  },
  {
    label: 'STEP 3',
    title: 'Your Piece Goes Live',
    body: 'Once approved, your listing is published on Dhobara. We handle the buyer experience, enquiries, and secure payment processing.',
  },
  {
    label: 'STEP 4',
    title: 'Ship & Get Paid',
    body: 'When your item sells, we send you a prepaid UPS label. Pack it, drop it off, and receive 80% of the sale price once the buyer\'s 3-day inspection window closes.',
  },
  {
    label: 'STEP 5',
    title: 'Track Everything',
    body: 'Your seller dashboard shows all your active listings, sales status, and payout history in one place.',
  },
];

const BUYER_STEPS = [
  {
    label: 'STEP 1',
    title: 'Browse Curated Pieces',
    body: 'Explore authentic South Asian designer wear from trusted sellers. Filter by designer, occasion, size, and price.',
  },
  {
    label: 'STEP 2',
    title: 'Secure Checkout',
    body: 'Purchase with confidence through our secure payment system. All transactions are protected end to end.',
  },
  {
    label: 'STEP 3',
    title: 'Fast Shipping',
    body: 'Items ship via UPS within 2 business days of purchase. Track your order every step of the way.',
  },
  {
    label: 'STEP 4',
    title: '3-Day Inspection Window',
    body: 'When your order arrives, you have 3 days to inspect it. If anything is not as described, we make it right.',
  },
  {
    label: 'STEP 5',
    title: 'Wear & Enjoy',
    body: 'Your new treasure is yours. Luxury South Asian fashion, at a price that makes sense.',
  },
];

const ASSURANCE = [
  {
    icon: '✦',
    title: 'Authenticity Verified',
    body: 'Every seller on Dhobara is asked for authenticity. We\'re actively building pre-authentication into our process for added buyer confidence.',
  },
  {
    icon: '◈',
    title: '3-Day Returns',
    body: 'Changed your mind or the item isn\'t as described? Initiate a return within 3 days of delivery and we\'ll handle the rest.',
  },
  {
    icon: '⬡',
    title: 'Secure Payments',
    body: 'All payments are processed securely. Your financial information is never shared with sellers.',
  },
];

const HowItWorksPage = () => {
  const [mode, setMode] = useState('sellers');
  const steps = mode === 'sellers' ? SELLER_STEPS : BUYER_STEPS;

  return (
    <Page
      title="How It Works | Buying & Selling Designer Wear on Dhobara"
      description="See how Dhobara works for sellers and buyers — list your designer piece in minutes, get studio-quality photos, secure payment, and fast UPS shipping."
      scrollingDisabled={false}
    >
    <DhobaraPageLayout>
      <div className={css.pageHeader}>
        <span className={css.eyebrow}>The Process</span>
        <h1 className={css.pageTitle}>How It <em>Works</em></h1>
        <p className={css.pageSubtitle}>
          Whether you're selling a cherished piece or discovering your next one, Dhobara makes it simple.
        </p>
      </div>

      <div className={css.toggleSection}>
        <div className={css.toggleWrap}>
          <button
            className={`${css.toggleBtn} ${mode === 'sellers' ? css.toggleBtnActive : ''}`}
            onClick={() => setMode('sellers')}
          >
            For Sellers
          </button>
          <button
            className={`${css.toggleBtn} ${mode === 'buyers' ? css.toggleBtnActive : ''}`}
            onClick={() => setMode('buyers')}
          >
            For Buyers
          </button>
        </div>
      </div>

      <div className={css.stepsSection}>
        <div className={css.stepsGrid}>
          {steps.map((step, i) => (
            <div key={step.title} className={css.step}>
              <div className={css.stepNumber}>0{i + 1}</div>
              <div className={css.stepContent}>
                <span className={css.stepLabel}>{step.label}</span>
                <h2 className={css.stepTitle}>{step.title}</h2>
                <p className={css.stepBody}>{step.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {mode === 'sellers' && (
        <div className={css.payoutBand}>
          <p className={css.payoutSplitLabel}>Payout Structure</p>
          <div className={css.payoutSplit}><span>80</span> / 20</div>
          <h2 className={css.payoutTitle}>You keep <em>80% of every sale</em></h2>
          <p className={css.payoutBody}>
            You take 80% of the purchase price, we take 20% to cover photography enhancement, listing management, buyer support, and secure payment processing. We release payment to you 3-days after the item is delivered to the Buyer.
          </p>
        </div>
      )}

      {mode === 'buyers' && (
        <div className={css.assuranceSection}>
          <span className={css.assuranceEyebrow}>Our Commitment</span>
          <h2 className={css.assuranceTitle}>Every purchase, <em>protected.</em></h2>
          <div className={css.assuranceGrid}>
            {ASSURANCE.map(card => (
              <div key={card.title} className={css.assuranceCard}>
                <span className={css.assuranceCardIcon}>{card.icon}</span>
                <h3 className={css.assuranceCardTitle}>{card.title}</h3>
                <p className={css.assuranceCardBody}>{card.body}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={css.ctaSection}>
        <p className={css.ctaTitle}>Ready to give your pieces a second life?</p>
        <Link to="/dashboard" className={css.btnCream}>Sell With Us</Link>
      </div>
    </DhobaraPageLayout>
    </Page>
  );
};

export default HowItWorksPage;
